import React, { useEffect, useMemo, useState } from 'react'
import { IonGrid, IonInput, IonLabel } from '@ionic/react'
import {
	Collection,
	CollectionItem,
	COLLECTION_LIST_LAYOUT_GRID,
	COLLECTION_SORT_BY_DISTANCE,
	sortItems,
	RootStoreState,
} from 'mytours-read-common'
import { getDirMode, getStyleProperty } from 'mobile_web/modules/platform'
import { getUserCurrentPosition } from '../../functions'
import TextPlaceholder from 'mobile_web/modules/common/components/placeholders/Text'
import classNames from 'classnames'
import { GetCollectionsShowResponse, MediaItem } from 'mytours-read-api-typescript'
import { ImageSizePreset, ImageSizePresetMap, ScreenSize } from 'common_lib/types'
import { generateImageUrl, getScreenSize } from 'common_lib/utils'
import CardImage from 'mobile_web/modules/common/components/card-image'
import { getImageHeight } from 'mobile_web/modules/screen/components/parts/functions'
import SectionMediaImage from 'mobile_web/modules/screen/components/parts/SectionMediaImage'
import { getImageAlt } from 'mobile_web/modules/common/functions'
import { InputChangeEventDetail } from '@ionic/core'
import { fade } from 'common_lib/services/color'
import SectionMediaAudioComponent from 'mobile_web/modules/screen/components/parts/SectionMediaAudio'
import SectionMediaVideoComponent from 'mobile_web/modules/screen/components/parts/SectionMediaVideo'
import { useSelector } from 'react-redux'
import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'
import { addToFavouritedKeys, collectionItemToFavouritedItem, removeFromFavouritedKeys } from 'mobile_web/modules/favourites/queries'

const ListLayoutEnum = GetCollectionsShowResponse.ListLayoutEnum

interface Props {
	collection: Collection
	items: CollectionItem[]
	onItemClicked: (item: CollectionItem, openInModal?: boolean) => () => void
	loading: boolean
}
const CollectionListContent: React.FC<Props> = ({ collection = {}, items, onItemClicked, loading }) => {
	const [orderedItems, setOrderedItems] = useState<CollectionItem[]>([])
	const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize())
	const [imageHeight, setImageHeight] = useState<number>(getImageHeight(screenSize))

	useEffect(() => {
		window.addEventListener('resize', updateScreenSize)

		return () => {
			window.removeEventListener('resize', updateScreenSize)
		}
	}, [])

	const updateScreenSize = (): void => {
		const newScreenSize = getScreenSize()

		if (newScreenSize !== screenSize) {
			setImageHeight(getImageHeight(screenSize))
			setScreenSize(newScreenSize)
		}
	}

	useEffect(() => {
		if (items.length === 0) {
			setOrderedItems([])
			return
		}
		sortItemsListener()
	}, [collection, items])

	const sortItemsListener = () => {
		// run position listener only if `sort_by` = DISTANCE
		if (!collection || collection?.sort_by !== COLLECTION_SORT_BY_DISTANCE) {
			// if `sort_by` is different then DISTANCE, just sort by provided method
			const itemsTmp = sortItems(items, collection?.sort_by)
			setOrderedItems(itemsTmp)
			return
		}

		getUserCurrentPosition(
			coordinates => {
				if (items.length === 0) {
					return
				}

				setOrderedItems(sortItems(items, collection?.sort_by, coordinates))
			},
			() => {
				setOrderedItems(sortItems(items, collection?.sort_by))
			},
		)
	}

	const renderIntroduction = useMemo(() => {
		const childrenProps = loading
			? { children: <TextPlaceholder /> }
			: { dangerouslySetInnerHTML: { __html: collection.description } }

		if (!loading && !collection.description) {
			return
		}

		return <div dir={getDirMode()} className="list-introduction list-block body-text" {...childrenProps} />
	}, [loading, collection.description])

	const renderMediaItem = useMemo(() => {
		const { preview_media_item: previewMediaItem, id, name } = collection

		if (!previewMediaItem) {
			return
		}

		let component: React.ReactElement

		switch (previewMediaItem.media_type) {
			case MediaItem.MediaTypeEnum.Audio: {
				component = (
					<SectionMediaAudioComponent
						audioPreview={previewMediaItem}
						container={{ type: 'list', id, title: name }}
						collectionId={id}
					/>
				)
				break
			}
			case MediaItem.MediaTypeEnum.Image: {
				component = (
					<div className="full-width" role="button">
						<SectionMediaImage file={previewMediaItem.file} alt={getImageAlt(previewMediaItem)} />
					</div>
				)
				break
			}
			case MediaItem.MediaTypeEnum.Video:
			case MediaItem.MediaTypeEnum.Webvideo: {
				component = (
					<SectionMediaVideoComponent
						sectionItem={previewMediaItem}
						height={imageHeight}
						isSingleItem={true}
						sectionId={id}
						sectionTitle={name}
						containerName={name}
					/>
				)
				break
			}
		}

		if (component) {
			return <div className="introduction-media-item">{component}</div>
		}

		return <div />
	}, [, collection])

	const [searchText, setSearchText] = useState<string>('')
	const filteredItems = useMemo(() => {
		if (!searchText) {
			return orderedItems
		}

		return orderedItems.filter(item => item.title.toLowerCase().includes(searchText.toLowerCase()))
	}, [orderedItems, searchText])

	const renderList = (): React.ReactElement => {
		const renderItems = loading ? Array(collection?.item_count || 4).fill({}) : filteredItems || []
		const itemTypeClass = loading ? ListLayoutEnum.Wide : collection?.list_layout || ListLayoutEnum.Grid

		const listLayout = collection?.list_layout || COLLECTION_LIST_LAYOUT_GRID
		const isListLayoutRow = listLayout === ListLayoutEnum.List

		return (
			<div className="list-block">
				<div className={classNames('list-items', itemTypeClass)}>
					{isListLayoutRow ? renderSearch(true) : null}
					{renderItems.map((item: CollectionItem, index: number) => renderItem(item, index))}
				</div>
			</div>
		)
	}

	const renderSearch = (isListRow: boolean): React.ReactElement => {
		if (loading) {
			return null
		}
		if (!collection.search_enabled) {
			return null
		}
		const borderColor = fade(getStyleProperty('--body-color'), 0.25)

		return (
			<div className={classNames('list-search', !isListRow ? 'list-search-wide-grid' : null)}>
				<IonInput
					value={searchText}
					onIonChange={(e: CustomEvent<InputChangeEventDetail>) => {
						setSearchText((e.target as HTMLInputElement).value)
					}}
					placeholder="Search..."
					className="list-search-ion-input"
					style={{ borderColor }}
				/>
				<div className="list-search-icons-container">
					<i className="fal fa-magnifying-glass list-search-loupe" aria-label="Search" aria-hidden="true"></i>
					<div className="list-search-close-container" onClick={() => setSearchText('')}>
						<i className="fal fa-times list-search-close" aria-label="Close" aria-hidden="true"></i>
					</div>
				</div>
			</div>
		)
	}

	const getImageSize = (layout?: GetCollectionsShowResponse.ListLayoutEnum) => {
		if (!layout) {
			layout = collection?.list_layout || ListLayoutEnum.Wide
		}

		const imageSizePreset = layout === ListLayoutEnum.Grid ? ImageSizePreset.Grid : ImageSizePreset.Row

		return {
			imageSizePreset,
			...ImageSizePresetMap[imageSizePreset],
		}
	}

	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)
	const dexieFavouritesKeys = useLiveQuery(() => db.favouritedKeys.toArray())
	const favouritesKeys = dexieFavouritesKeys ? dexieFavouritesKeys.map((key) => key.key) : []
	
	const renderItem = (item: CollectionItem, index: number): React.ReactNode => {
		const key = `${item.item_id}-${item.item_type}`
		const isFavourited = favouritesKeys.includes(key)
		const toggleFavorite: React.MouseEventHandler<HTMLDivElement> = (e) => {
			e.stopPropagation()
			const favouritedItem = collectionItemToFavouritedItem(item)
			if (!isFavourited) {
				addToFavouritedKeys(key, favouritedItem)
			}
			else {
				removeFromFavouritedKeys(key)
			}
		}
	
		const size = getImageSize()

		const listLayout = collection?.list_layout || COLLECTION_LIST_LAYOUT_GRID
		
		const coverImageGridUrl = listLayout === ListLayoutEnum.Grid ? item?.cover_image_grid_url_thumb : item?.cover_image_grid_url_display
		const coverImageUrl = listLayout === ListLayoutEnum.Grid ? item?.cover_image_url_thumb : item?.cover_image_url_display

		const coverImageGridFocalPoint = item?.cover_image_grid?.file?.focal_point
		const coverImageFocalPoint = item?.cover_image?.file?.focal_point
		const coverImageGridCrop =
			coverImageGridFocalPoint && (coverImageGridFocalPoint[0] !== 0.5 || coverImageGridFocalPoint[1] !== 0.5)
		const coverImageCrop = coverImageFocalPoint && (coverImageFocalPoint[0] !== 0.5 || coverImageFocalPoint[1] !== 0.5)

		const coverImageGridOptions = {
			crop: coverImageGridCrop,
			focalPoint: coverImageGridFocalPoint,
		}

		const coverImageOptions = {
			crop: coverImageCrop,
			focalPoint: coverImageFocalPoint,
		}

		const coverImageGrid =
			coverImageGridUrl ||
			(item?.cover_image_grid?.file &&
			generateImageUrl(item?.cover_image_grid.file, {
				...size,
				...coverImageGridOptions,
			}))

		const coverImageGrid2x =
			item?.cover_image_grid?.file &&
			generateImageUrl(item?.cover_image_grid.file, {
				width: size.width * 2,
				height: size.height * 2,
				...coverImageGridOptions,
			})

		const coverImage =
			coverImageUrl ||
			(item?.cover_image?.file &&
			generateImageUrl(item?.cover_image.file, {
				...size,
				...coverImageOptions,
			}))

		const coverImage2x =
			item?.cover_image?.file &&
			generateImageUrl(item?.cover_image.file, {
				width: size.width * 2,
				height: size.height * 2,
				...coverImageOptions,
			})

		switch (listLayout) {
			case ListLayoutEnum.Grid: {
				return (
					<CardImage
						key={index}
						loading={loading}
						className="collection-list-item list-card-image"
						image={coverImageGrid ? coverImageGrid : coverImage}
						image2x={coverImageGrid2x ? coverImageGrid2x : coverImage2x}
						label={item.title}
						role="button"
						onClick={onItemClicked(item)}
						itemId={item.item_id}
						itemType={item.item_type}
						item={item}
						appMetaData={appMetaData}
						toggleFavorite={toggleFavorite}
						isFavourited={isFavourited}
					/>
				)
			}

			case ListLayoutEnum.Wide: {
				return (
					<CardImage
						key={index}
						loading={loading}
						className="collection-list-item list-card-image"
						image={coverImage ? coverImage : coverImageGrid}
						image2x={coverImage2x ? coverImage2x : coverImageGrid2x}
						label={item.title}
						role="button"
						onClick={onItemClicked(item)}
						itemId={item.item_id}
						itemType={item.item_type}
						item={item}
						appMetaData={appMetaData}
						toggleFavorite={toggleFavorite}
						isFavourited={isFavourited}
					/>
				)
			}
			case ListLayoutEnum.List: {
				return (
					<IonLabel key={index} className="collection-list-item list-row-item" onClick={onItemClicked(item)}>
						{item?.title || ''}
					</IonLabel>
				)
			}
			default: {
				return null
			}
		}
	}

	const listLayout = collection?.list_layout || COLLECTION_LIST_LAYOUT_GRID
	const isListLayoutRow = listLayout === ListLayoutEnum.List

	return (
		<IonGrid className="list-container">
			{renderIntroduction}
			{renderMediaItem}
			{!isListLayoutRow ? renderSearch(false) : null}
			{renderList()}
		</IonGrid>
	)
}

export default CollectionListContent
